import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Link,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../api';

/** Column names mean nothing to an admin; these are what the review table shows. */
const FIELD_LABELS = {
  address: 'Address',
  phone: 'Phone',
  email: 'Email',
  website: 'Website',
  latitude: 'Latitude',
  longitude: 'Longitude',
  gender: 'Intake',
  target_school_id: 'School category',
  region_id: 'Region',
  exam_board_id: 'Exam board',
  selection_type: 'Selection type',
  consortium_name: 'Consortium / test group',
  exam_provider: 'Exam provider',
  question_style: 'Question style',
  reasoning_included: 'Reasoning included',
  subjects_tested: 'Subjects tested',
  entry_format: 'Entry format',
  pass_criteria: 'Pass criteria',
  open_day_date: 'Open day date',
  open_day_notes: 'Open day notes',
  exam_date: 'Exam date',
  exam_date_notes: 'Exam date notes',
  registration_deadline: 'Registration deadline',
  registration_notes: 'Registration notes',
  admission_notes: 'Admissions notes',
};

const POLL_INTERVAL_MS = 3000;

const hostOf = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
};

const truncate = (value, max = 140) => {
  const str = value === null || value === undefined || value === '' ? '—' : String(value);
  return str.length > max ? `${str.slice(0, max)}…` : str;
};

/**
 * Reviews what the AI found before any of it reaches the database.
 *
 * A field is pre-ticked only when we currently hold nothing for it. Anything that
 * would overwrite an existing value starts unticked, so replacing data is always a
 * deliberate act.
 */
export function SchoolEnrichmentReviewDialog({ open, runId, onClose, onApplied }) {
  const [run, setRun] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [selected, setSelected] = useState({});
  const pollRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const load = useCallback(async () => {
    if (!runId) return;
    try {
      const response = await api.get(`admin/master-form/schools/enrich-runs/${runId}`, {
        params: { status: 'pending', per_page: 100 },
      });
      const data = response.data?.data || {};
      setRun(data.run || null);
      setResults(data.results || []);

      if (data.run?.finished) {
        stopPolling();
        setLoading(false);
      }
    } catch (error) {
      stopPolling();
      setLoading(false);
      toast.error(error.response?.data?.message || 'Could not load the enrichment run');
    }
  }, [runId, stopPolling]);

  useEffect(() => {
    if (!open || !runId) {
      stopPolling();
      return undefined;
    }

    setLoading(true);
    setSelected({});
    load();
    pollRef.current = setInterval(load, POLL_INTERVAL_MS);

    return stopPolling;
  }, [open, runId, load, stopPolling]);

  // Pre-tick the fields that only fill a gap, never the ones that overwrite.
  useEffect(() => {
    setSelected((previous) => {
      const next = { ...previous };
      results.forEach((result) => {
        if (next[result.id]) return;
        const fields = {};
        Object.keys(result.proposed || {}).forEach((field) => {
          const current = result.current?.[field];
          fields[field] = current === null || current === undefined || current === '';
        });
        next[result.id] = fields;
      });
      return next;
    });
  }, [results]);

  const toggleField = (resultId, field) => {
    setSelected((previous) => ({
      ...previous,
      [resultId]: { ...previous[resultId], [field]: !previous[resultId]?.[field] },
    }));
  };

  const setAllForResult = (result, value) => {
    setSelected((previous) => ({
      ...previous,
      [result.id]: Object.keys(result.proposed || {}).reduce(
        (acc, field) => ({ ...acc, [field]: value }),
        {}
      ),
    }));
  };

  const selectionPayload = useMemo(
    () =>
      results
        .map((result) => ({
          result_id: result.id,
          fields: Object.entries(selected[result.id] || {})
            .filter(([, ticked]) => ticked)
            .map(([field]) => field),
        }))
        .filter((selection) => selection.fields.length > 0),
    [results, selected]
  );

  const tickedCount = selectionPayload.reduce((sum, s) => sum + s.fields.length, 0);

  const handleApply = async () => {
    if (selectionPayload.length === 0) return;
    setApplying(true);
    try {
      const response = await api.post(
        `admin/master-form/schools/enrich-runs/${runId}/apply`,
        { selections: selectionPayload }
      );
      toast.success(response.data?.message || 'Schools updated');
      onApplied?.();
      onClose?.();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not apply the selected changes');
    } finally {
      setApplying(false);
    }
  };

  const inProgress = run && !run.finished;

  return (
    <Dialog open={open} onClose={applying ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        Review web data
        {run?.entry_year ? (
          <Typography component="span" sx={{ ml: 1, color: 'text.secondary', fontSize: 14 }}>
            {run.entry_year} entry
          </Typography>
        ) : null}
      </DialogTitle>

      <DialogContent dividers>
        {inProgress && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Researching schools… {run.processed} of {run.total || '?'} done
              {run.changed ? `, ${run.changed} with something new` : ''}
            </Typography>
            <LinearProgress
              variant={run.total ? 'determinate' : 'indeterminate'}
              value={run.total ? (run.processed / run.total) * 100 : 0}
            />
          </Box>
        )}

        {loading && !run && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {run?.status === 'failed' && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {run.error_message || 'The run failed.'}
          </Alert>
        )}

        {run?.finished && results.length === 0 && (
          <Alert severity="info">
            Nothing new to review — the web sources agree with what is already stored, or no
            official pages could be found.
          </Alert>
        )}

        {results.length > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Fields that fill an empty value are ticked for you. Anything that would replace data you
            already hold is left unticked on purpose — tick it only if the new value is better.
          </Alert>
        )}

        {results.map((result) => {
          const fields = Object.entries(result.proposed || {});
          return (
            <Accordion key={result.id} defaultExpanded={results.length === 1}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600, flexGrow: 1 }}>{result.school_name}</Typography>
                <Chip size="small" label={`${fields.length} field(s)`} sx={{ mr: 1 }} />
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ mb: 1 }}>
                  <Button size="small" onClick={() => setAllForResult(result, true)}>
                    Select all
                  </Button>
                  <Button size="small" onClick={() => setAllForResult(result, false)}>
                    Select none
                  </Button>
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" />
                      <TableCell sx={{ fontWeight: 700 }}>Field</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Currently</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Found on the web</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Source</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fields.map(([field, proposal]) => {
                      const current = result.current?.[field];
                      const overwrites = current !== null && current !== undefined && current !== '';
                      return (
                        <TableRow key={field} hover>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={Boolean(selected[result.id]?.[field])}
                              onChange={() => toggleField(result.id, field)}
                            />
                          </TableCell>
                          <TableCell>
                            {FIELD_LABELS[field] || field}
                            {overwrites && (
                              <Tooltip title="This would replace a value you already have">
                                <Chip
                                  size="small"
                                  color="warning"
                                  label="replaces"
                                  sx={{ ml: 1, height: 18, fontSize: 11 }}
                                />
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell sx={{ color: 'text.secondary', maxWidth: 240 }}>
                            {truncate(current, 90)}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 320 }}>{truncate(proposal.display)}</TableCell>
                          <TableCell sx={{ maxWidth: 180 }}>
                            <Link
                              href={proposal.source_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ fontSize: 12 }}
                            >
                              {hostOf(proposal.source_url)}
                            </Link>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={applying}>
          Close
        </Button>
        <Button
          variant="contained"
          onClick={handleApply}
          disabled={applying || tickedCount === 0}
          startIcon={applying ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {applying ? 'Applying…' : `Apply ${tickedCount} field(s)`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const BUCKET_TABS = [
  { key: 'junk', label: 'Not a school', help: 'Form labels and exam boards the PDF importer read as schools. These get removed.' },
  { key: 'duplicate', label: 'Duplicates', help: 'Papers and mock exams move to the surviving school, then the duplicate is removed.' },
  { key: 'real', label: 'Real, unverified', help: 'Genuine schools that were never activated. These become active.' },
];

/**
 * Clean-up for the rows the question-bank PDF importer created from cover-page
 * labels. Rows the classifier is unsure about arrive unticked and say so.
 */
export function SchoolTriageDialog({ open, onClose, onApplied }) {
  const [buckets, setBuckets] = useState({ junk: [], duplicate: [], real: [] });
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [tab, setTab] = useState(0);
  const [ticked, setTicked] = useState({});

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    setLoading(true);
    api
      .get('admin/master-form/schools/triage')
      .then((response) => {
        if (cancelled) return;
        const data = response.data?.data?.buckets || { junk: [], duplicate: [], real: [] };
        setBuckets(data);
        // Confident rows start ticked; guesses need a human to agree first.
        const initial = {};
        Object.values(data).flat().forEach((row) => {
          initial[row.school_id] = row.confident !== false;
        });
        setTicked(initial);
      })
      .catch((error) => {
        if (!cancelled) toast.error(error.response?.data?.message || 'Could not load the clean-up list');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open]);

  const decisions = useMemo(
    () =>
      Object.values(buckets)
        .flat()
        .filter((row) => ticked[row.school_id])
        .map((row) => ({
          school_id: row.school_id,
          action: row.suggested_action,
          ...(row.merge_into_id ? { merge_into_id: row.merge_into_id } : {}),
        })),
    [buckets, ticked]
  );

  const handleApply = async () => {
    if (decisions.length === 0) return;
    setApplying(true);
    try {
      const response = await api.post('admin/master-form/schools/triage/apply', { decisions });
      toast.success(response.data?.message || 'Clean-up applied');
      onApplied?.();
      onClose?.();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not apply the clean-up');
    } finally {
      setApplying(false);
    }
  };

  const activeBucket = BUCKET_TABS[tab];
  const rows = buckets[activeBucket.key] || [];

  return (
    <Dialog open={open} onClose={applying ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Clean up unverified schools</DialogTitle>
      <DialogContent dividers>
        <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 2 }}>
          {BUCKET_TABS.map((bucket) => (
            <Tab key={bucket.key} label={`${bucket.label} (${(buckets[bucket.key] || []).length})`} />
          ))}
        </Tabs>

        <Alert severity="info" sx={{ mb: 2 }}>
          {activeBucket.help}
        </Alert>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell sx={{ fontWeight: 700 }}>School</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>What happens</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} sx={{ color: 'text.secondary' }}>
                    Nothing in this group.
                  </TableCell>
                </TableRow>
              )}
              {rows.map((row) => (
                <TableRow key={row.school_id} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={Boolean(ticked[row.school_id])}
                      onChange={() =>
                        setTicked((previous) => ({
                          ...previous,
                          [row.school_id]: !previous[row.school_id],
                        }))
                      }
                    />
                  </TableCell>
                  <TableCell>
                    {row.name}
                    {row.confident === false && (
                      <Chip
                        size="small"
                        color="warning"
                        label="not sure"
                        sx={{ ml: 1, height: 18, fontSize: 11 }}
                      />
                    )}
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>
                    {row.suggested_action === 'merge'
                      ? `Merge into ${row.merge_into_name}`
                      : row.suggested_action === 'soft_delete'
                      ? 'Remove from the list'
                      : 'Mark as active'}
                    {row.reason ? ` — ${row.reason}` : ''}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={applying}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="warning"
          onClick={handleApply}
          disabled={applying || decisions.length === 0}
          startIcon={applying ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {applying ? 'Applying…' : `Apply to ${decisions.length} school(s)`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
